import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { Header } from "@/components/Header";
import { AnimatedSection } from "@/components/AnimatedSection";
import { Send, MessageCircle } from "lucide-react";
import { cadastroSchema, type CadastroFormData } from "@/lib/validations/contact";

const Cadastro = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState<CadastroFormData>({
    nome: "",
    email: "",
    telefone: "",
    empresa: "",
    mensagem: "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof CadastroFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: keyof CadastroFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    const result = cadastroSchema.safeParse(formData);

    if (!result.success) {
      const fieldErrors: Partial<Record<keyof CadastroFormData, string>> = {};
      result.error.errors.forEach(error => {
        const field = error.path[0] as keyof CadastroFormData;
        fieldErrors[field] = error.message;
      });
      setErrors(fieldErrors);
      setIsSubmitting(false);
      return;
    }

    // Form data is validated - in production, this would be sent to a backend
    toast({
      title: "Formulário enviado!",
      description: "Entraremos em contato em breve.",
    });

    navigate('/');
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-blue-600 relative overflow-x-hidden">
      <Header />
      
      <div className="pt-20 sm:pt-24">
        {/* Hero Section with Gradient */}
        <section className="relative w-full">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-950"></div>
          <article className="relative z-10 flex flex-col justify-center items-center text-center xl:max-w-5xl gap-4 text-xl text-white mx-auto py-16 px-4 lg:py-24 lg:px-16 xl:px-28">
            <MessageCircle className="w-16 h-16 mb-2" />
            <h1 className="text-3xl sm:text-4xl font-bold">Fale Conosco</h1>
            <p className="max-w-2xl">
              Entre em contato conosco para saber mais sobre nossas soluções em recursos hídricos. 
              Nossa equipe está pronta para atender suas necessidades.
            </p>
          </article>
        </section>

        {/* Form Section */}
        <section className="py-10 sm:py-16 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 max-w-xl">
            <AnimatedSection delay={100}>
              <div className="bg-white p-6 sm:p-8 rounded-lg shadow-lg">
                <h2 className="text-2xl font-bold text-blue-600 mb-6 text-center">
                  Envie sua Mensagem
                </h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="nome" className="text-gray-700 font-medium">
                      Nome Completo
                    </Label>
                    <Input
                      id="nome"
                      type="text"
                      value={formData.nome}
                      onChange={(e) => handleChange("nome", e.target.value)}
                      className={`mt-1.5 ${errors.nome ? "border-red-500" : ""}`}
                      placeholder="Digite seu nome completo"
                      maxLength={100}
                    />
                    {errors.nome && (
                      <p className="text-sm text-red-500 mt-1">{errors.nome}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium">
                      E-mail
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className={`mt-1.5 ${errors.email ? "border-red-500" : ""}`}
                      placeholder="Digite seu e-mail"
                      maxLength={255}
                    />
                    {errors.email && (
                      <p className="text-sm text-red-500 mt-1">{errors.email}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="telefone" className="text-gray-700 font-medium">
                      Telefone
                    </Label>
                    <Input
                      id="telefone"
                      type="tel"
                      value={formData.telefone}
                      onChange={(e) => handleChange("telefone", e.target.value)}
                      className={`mt-1.5 ${errors.telefone ? "border-red-500" : ""}`}
                      placeholder="(XX) XXXXX-XXXX"
                      maxLength={20}
                    />
                    {errors.telefone && (
                      <p className="text-sm text-red-500 mt-1">{errors.telefone}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="empresa" className="text-gray-700 font-medium">
                      Empresa
                    </Label>
                    <Input
                      id="empresa"
                      type="text"
                      value={formData.empresa}
                      onChange={(e) => handleChange("empresa", e.target.value)}
                      className={`mt-1.5 ${errors.empresa ? "border-red-500" : ""}`}
                      placeholder="Nome da sua empresa"
                      maxLength={100}
                    />
                    {errors.empresa && (
                      <p className="text-sm text-red-500 mt-1">{errors.empresa}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="mensagem" className="text-gray-700 font-medium">
                      Mensagem
                    </Label>
                    <textarea
                      id="mensagem"
                      value={formData.mensagem}
                      onChange={(e) => handleChange("mensagem", e.target.value)}
                      className={`mt-1.5 w-full min-h-[120px] rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
                        errors.mensagem ? "border-red-500" : "border-input"
                      }`}
                      placeholder="Como podemos ajudá-lo?"
                      maxLength={2000}
                    />
                    {errors.mensagem && (
                      <p className="text-sm text-red-500 mt-1">{errors.mensagem}</p>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-blue-600 hover:bg-blue-700 hover:scale-105 transition-all duration-200 py-3 text-base font-semibold"
                    disabled={isSubmitting}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {isSubmitting ? "Enviando..." : "Enviar Mensagem"}
                  </Button>
                </form>
              </div>
            </AnimatedSection>

            {/* Contact Info Cards */}
            <AnimatedSection className="mt-8 grid sm:grid-cols-2 gap-4" delay={200}>
              <a 
                href="https://api.whatsapp.com/send?phone=5511986162105&text=Ol%C3%A1%2C%20" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <div className="bg-green-500 p-3 rounded-full">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">WhatsApp</p>
                  <p className="text-sm text-gray-600">(11) 98616-2105</p>
                </div>
              </a>
              
              <a 
                href="mailto:contato@ahqconsultoriaemengenharia.com.br"
                className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <div className="bg-blue-600 p-3 rounded-full">
                  <Send className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800">E-mail</p>
                  <p className="text-sm text-gray-600 break-all">contato@ahqconsultoria...</p>
                </div>
              </a>
            </AnimatedSection>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Cadastro;
